import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, type ApiClient } from '../../api/client';
import type { CtInterviewForm, CtInterviewQuestion } from '../../api/types';
import { ClinicField } from '../../components/ClinicField';
import { Modal } from '../../components/Modal';
import { ReadAloud } from '../../components/ReadAloud';
import { useClinicDirectory } from '../../hooks/useClinicDirectory';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

/*
 * The wording the page prints and the wording it reads aloud, kept in one place so they cannot
 * drift apart.
 *
 * Everything else the assessment says comes from the API, which the read-aloud takes straight from
 * the same response the form renders. These four are the page's own, and they are exactly the ones
 * somebody listening most needs: the two boxes at the top, and the fact that handing in is final.
 * Written twice, a reworded button would quietly leave the spoken version telling people to press
 * something that is no longer there.
 */
const candidateNameLabel = 'Your name';
const locationLabel = 'Location';
const locationHint = 'Which clinic you are at';
const handInLabel = 'Hand in my answers';
const handInWarning = 'You cannot change your answers once you hand them in.';

/**
 * The CT candidate skills assessment, as the candidate fills it in.
 *
 * Public: no sign-in, no hub shell, no sidebar — see `PublicApp`. The questions come from
 * `GET /api/public/ct-interview/form` rather than living here, so the clinic team can reword the
 * assessment without a frontend change.
 *
 * Nothing is kept in `localStorage`, deliberately. The assessment is normally done on the
 * candidate's own device before their clinic visit, but the URL is open and nothing stops it being
 * opened on a shared front-desk machine, where a draft surviving in the browser would hand one
 * candidate's answers to whoever sits down next — the same reasoning that keeps MSAL's token cache
 * in `sessionStorage` (see `authConfig.ts`). The cost is that a refresh loses the answers, which
 * `beforeunload` below is there to warn about.
 */
export function CtInterviewFormPage({ api }: { api: ApiClient }) {
  const [form, setForm] = useState<CtInterviewForm | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [candidateName, setCandidateName] = useState('');
  const [location, setLocation] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});

  // The clinic directory the location dropdown is filled from, on its own call and with its own
  // failure: an unreachable directory costs that one field its list, never the assessment. See
  // useClinicDirectory and ClinicField for what the candidate gets instead.
  const loadClinics = useCallback((signal: AbortSignal) => api.listPublicClinics(signal), [api]);
  const clinics = useClinicDirectory(loadClinics);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  /** Set once the API has accepted the assessment; from then on this page is the thank-you screen. */
  const [submitted, setSubmitted] = useState(false);

  /**
   * The banner above the form, focused when a hand-in is turned away — see `failSubmit` below.
   *
   * Null on every render that has no error to show, which is why the effect guards on the ref rather
   * than assuming it.
   */
  const errorRef = useRef<HTMLParagraphElement>(null);

  // The title the API gives, once there is one. Before that it says what the page is doing, which is
  // the honest answer and better than the bundle's "HR Hub" either way.
  useDocumentTitle(form ? `${form.title} — TVG-Medulla` : 'Loading the assessment — TVG-Medulla');

  /**
   * How many times the banner has been raised, used as its key so that each refusal mounts a fresh
   * one. See `failSubmit`.
   */
  const [errorAttempt, setErrorAttempt] = useState(0);

  /*
   * Moving focus to the error, not just the scrollbar.
   *
   * `window.scrollTo` in `failSubmit` puts the banner on screen, which settles it for anyone watching
   * the screen and does nothing at all for anyone who is not: a candidate on a screen reader presses
   * "Hand in my answers", hears nothing, and is left at the bottom of a form that has quietly refused
   * them. Focus is what carries the reading point, so it goes to the message.
   *
   * Keyed on the attempt rather than on the text, because neither mechanism fires on its own when the
   * same banner is raised twice: `focus()` on the element that already holds focus does nothing, and
   * role="alert" on text that has not changed announces nothing. A candidate who presses "Hand in my
   * answers" twice with the same field blank heard it once. The key makes every refusal a new
   * element, so there is a new live region to announce and an unfocused one to focus.
   */
  useEffect(() => {
    if (submitError) errorRef.current?.focus();
  }, [submitError, errorAttempt]);

  /**
   * Refuse the hand-in: say why, put the reason on screen, and send the reading point to it.
   *
   * Every failure path goes through here so that none of them can forget one of the three — the
   * scroll without the focus is the form that fails silently for a screen reader.
   */
  const failSubmit = useCallback((message: string) => {
    setSubmitError(message);
    setErrorAttempt((attempt) => attempt + 1);
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    api
      .getCtInterviewForm(controller.signal)
      .then(setForm)
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;

        // The candidate gets one sentence they can act on; the detail goes to the console for
        // whoever is standing next to them trying to work out why the page is blank.
        console.error('Could not load the CT interview form', error);
        setLoadError('The assessment could not be loaded. Check the connection and reload the page.');
      });

    return () => controller.abort();
  }, [api]);

  const questions: CtInterviewQuestion[] = useMemo(
    () => form?.sections.flatMap((section) => section.questions) ?? [],
    [form],
  );

  /**
   * The form as one piece of spoken text, for the "read this page aloud" control.
   *
   * Built from the same data the page renders rather than scraped back out of the DOM, so what is
   * read is what the API sent. The numbering is spoken in words — "Section 1" and "Question 3" are
   * read as the page prints them, where "Q3" would come out as a letter and a digit.
   *
   * The candidate's own answers are deliberately not in here: this reads the assessment, not what
   * has been typed into it, and a form that read somebody's half-finished answers back across a
   * front desk would be its own problem.
   */
  const spokenPage = useMemo(() => {
    if (!form) return '';

    const parts: string[] = [
      form.title,
      ...form.instructions,

      // The two boxes above the questions, said as a sentence rather than as two labels. Read out on
      // their own, "Your name. Location." is two phrases with nothing tying them to the boxes on
      // screen; the counting is what makes them findable by somebody who can see the page perfectly
      // well and is listening because reading it is the hard part.
      //
      // Which kind of control the second one is gets said too, because a list and a text box are not
      // the same instruction: the directory is read at every page load, and on the load where it
      // cannot be reached this really is a box to type into.
      `This form has two boxes to fill in at the top. The first is ${candidateNameLabel}. ` +
        `The second is ${locationLabel} — ${locationHint}, ` +
        `${clinics.state === 'unavailable' ? 'typed in' : 'chosen from a list'}.`,
    ];

    form.sections.forEach((section, sectionIndex) => {
      parts.push(`Section ${sectionIndex + 1}. ${section.title}.`);
      if (section.guidance) parts.push(section.guidance);

      section.questions.forEach((question, questionIndex) => {
        parts.push(`Question ${questionIndex + 1}. ${question.prompt}`);

        // Each option on its own line, prefixed with the word "Option" rather than left as a bare
        // letter: "A." on its own is read as the word "a" by most voices, which turns four distinct
        // choices into four sentences that all start the same way and none of which can be chosen by
        // name. The letters are what the radio buttons are labelled with on screen, so they have to
        // come back out of the speech the same way.
        question.options.forEach((option) => parts.push(`Option ${option.id}. ${option.text}`));
      });
    });

    // The end of the page, which is the part it would be worst to leave out: a candidate who has
    // heard the whole assessment and never heard that handing in is final is the one the confirmation
    // dialog exists to catch, arriving at it with no idea what it is asking.
    parts.push(
      `When you have finished, choose ${handInLabel} at the bottom of the page. ${handInWarning}`,
    );

    return parts.join('\n');
  }, [form, clinics.state]);

  const hasContent =
    candidateName.trim().length > 0 ||
    location.trim().length > 0 ||
    Object.values(answers).some((value) => value.trim().length > 0);

  // A refresh or a closed tab throws the answers away, since nothing is persisted between loads.
  // Browsers show their own wording here; the string only has to be non-empty to arm the prompt.
  //
  // It disarms at the hand-in: the API has the answers by then, and the candidate is left nothing
  // on the page that closing it would cost them.
  const atRisk = !submitted && hasContent;

  useEffect(() => {
    if (!atRisk) return;

    function warn(event: BeforeUnloadEvent) {
      event.preventDefault();
    }

    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [atRisk]);

  const unanswered = questions.filter((question) => (answers[question.id] ?? '').trim().length === 0);

  /**
   * The same blanks, but as one line per section — "Section 1: Problem-Solving — Q2, Q4".
   *
   * Listing each question in full made the confirmation dialog scroll, which defeats it: a
   * candidate who has to scroll a warning to reach the button reads neither. The numbers are the
   * ones printed beside the questions on the form they are looking at, so this is a map back to
   * the page rather than a restatement of it.
   */
  const unansweredBySection = useMemo(
    () =>
      (form?.sections ?? [])
        .map((section, sectionIndex) => ({
          id: section.id,
          label: `Section ${sectionIndex + 1}: ${section.title}`,
          numbers: section.questions
            .map((question, questionIndex) => ({ question, number: questionIndex + 1 }))
            .filter(({ question }) => (answers[question.id] ?? '').trim().length === 0)
            .map(({ number }) => `Q${number}`),
        }))
        .filter((section) => section.numbers.length > 0),
    [form, answers],
  );

  const submit = useCallback(async () => {
    if (!form) return;

    // The dialog stays up for the duration and closes in the `finally` below, so the confirm button
    // it was clicked on is the thing that shows the request running. Closing it first would drop
    // the candidate back onto a form that looks untouched while their answers are in flight.
    setSubmitting(true);
    setSubmitError(null);
    setFieldErrors({});

    try {
      await api.submitCtInterview({
        candidateName: candidateName.trim(),
        location: location.trim(),
        formVersion: form.version,
        answers,
      });

      setSubmitted(true);
      window.scrollTo({ top: 0 });
    } catch (error: unknown) {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors);
        failSubmit(
          Object.keys(error.fieldErrors).length > 0
            ? 'Check the highlighted answers and hand it in again.'
            : error.status === 429
              ? 'Too many submissions from this computer just now. Wait a minute and try again.'
              : error.message,
        );
      } else {
        failSubmit('Your answers could not be sent. Check the connection and try again.');
      }
    } finally {
      setSubmitting(false);

      // Either way: on success the page swaps to the thank-you screen, and on failure the error
      // belongs on the form behind this dialog, not inside it.
      setConfirmOpen(false);
    }
  }, [api, form, candidateName, location, answers, failSubmit]);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    // The header fields are the only hard requirements — an assessment nobody can attribute to a
    // candidate is not worth keeping. Blank answers are the candidate's call, confirmed below.
    const missing: Record<string, string[]> = {};
    if (candidateName.trim().length === 0) missing.candidateName = ['Enter your name.'];

    // Worded for the control that is actually on screen: the field is a dropdown on every load the
    // clinic directory answers, and a text box on the ones it does not.
    if (location.trim().length === 0) {
      missing.location = [
        clinics.state === 'unavailable' ? 'Enter the clinic you are at.' : 'Choose the clinic you are at.',
      ];
    }

    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      failSubmit('Fill in your name and the clinic before handing this in.');
      return;
    }

    // Always confirmed, not only when something was left blank. Handing in is the one irreversible
    // thing a candidate does on this page — there is no edit afterwards — and a button that does
    // it on the first click is a trap next to a Tab key.
    setConfirmOpen(true);
  }

  if (loadError) {
    return (
      <PublicPage>
        <div className="public-card public-card--message">
          <h1 className="public-card__title">Something went wrong</h1>
          <p>{loadError}</p>
        </div>
      </PublicPage>
    );
  }

  if (!form) {
    return (
      <PublicPage>
        <div className="public-card public-card--message">
          <p>Loading the assessment…</p>
        </div>
      </PublicPage>
    );
  }

  // The whole screen, deliberately: the candidate hands the assessment in and is done. There is no
  // copy to take away and nothing further to do, so anything else here would only invite a question
  // the page cannot answer — and the second line is there to close the one question it does invite,
  // which is the packet's own closing note about not having to print or send anything.
  if (submitted) {
    return (
      <PublicPage>
        <div className="public-card public-card--message">
          <h1 className="public-card__title">Thank you for completing the assessment.</h1>
          <p>
            Your responses are on file — there is no need to download, print or email anything.
          </p>
        </div>
      </PublicPage>
    );
  }

  return (
    <PublicPage>
      <form className="public-card interview" onSubmit={handleSubmit} noValidate>
        <header className="interview__header">
          <p className="interview__org">TVG-Medulla LLC</p>
          <h1 className="public-card__title">{form.title}</h1>
        </header>

        {/* Above the instructions rather than below them: someone who needs this cannot read the
            instructions to find out that it is here. */}
        <ReadAloud text={spokenPage} />

        {/* One tinted box, a paragraph per entry — the packet prints them together and the second
            only makes sense after the first. */}
        <div className="interview__instructions">
          {form.instructions.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>

        {submitError && (
          <p key={errorAttempt} className="interview__error" role="alert" tabIndex={-1} ref={errorRef}>
            {submitError}
          </p>
        )}

        <div className="interview__identity">
          <TextField
            id="candidateName"
            label={candidateNameLabel}
            value={candidateName}
            maxLength={form.candidateNameMaxLength}
            errors={fieldErrors.candidateName}
            autoComplete="name"
            onChange={setCandidateName}
          />
          <ClinicField
            id="location"
            label={locationLabel}
            clinics={clinics}
            value={location}
            maxLength={form.locationMaxLength}
            errors={fieldErrors.location}
            hint={locationHint}
            onChange={setLocation}
          />
        </div>

        {form.sections.map((section, sectionIndex) => (
          <section className="interview__section" key={section.id}>
            <h2 className="interview__section-title">
              Section {sectionIndex + 1}: {section.title}
            </h2>

            {section.guidance && <p className="interview__guidance">{section.guidance}</p>}

            {section.questions.map((question, questionIndex) => (
              <AnswerField
                key={question.id}
                question={question}
                number={questionIndex + 1}
                value={answers[question.id] ?? ''}
                errors={fieldErrors[`answers.${question.id}`]}
                onChange={(value) => setAnswers((current) => ({ ...current, [question.id]: value }))}
              />
            ))}
          </section>
        ))}

        <div className="interview__actions">
          <button type="submit" className="button button--primary button--wide" disabled={submitting}>
            {submitting ? 'Sending…' : handInLabel}
          </button>
          <p className="interview__hint">{handInWarning}</p>
        </div>
      </form>

      <Modal
        open={confirmOpen}
        title={unanswered.length > 0 ? 'Hand in with unanswered questions?' : 'Hand in your answers?'}
        onClose={() => setConfirmOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="button button--quiet"
              disabled={submitting}
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button button--primary"
              disabled={submitting}
              onClick={() => void submit()}
            >
              {submitting ? 'Sending…' : 'Submit'}
            </button>
          </>
        }
      >
        <p className="interview__confirm-lead">
          Once you hand this in you cannot change your answers.
        </p>

        {unanswered.length > 0 && (
          <>
            <p className="interview__confirm-lead">
              {unanswered.length === 1
                ? 'One question has been left blank:'
                : `${unanswered.length} questions have been left blank:`}
            </p>
            <ul className="interview__confirm-list">
              {unansweredBySection.map((section) => (
                <li key={section.id}>
                  <strong>{section.label}</strong> — {section.numbers.join(', ')}
                </li>
              ))}
            </ul>
          </>
        )}
      </Modal>
    </PublicPage>
  );
}

/** The plain, chrome-free frame every public page sits in. */
function PublicPage({ children }: { children: React.ReactNode }) {
  return <main className="public-page">{children}</main>;
}

function TextField({
  id,
  label,
  value,
  maxLength,
  errors,
  autoComplete,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  maxLength: number;
  errors?: string[];
  autoComplete?: string;
  onChange: (value: string) => void;
}) {
  const invalid = (errors?.length ?? 0) > 0;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {/* Hidden from the reading because `required` below says the same thing in the one way a
            screen reader states it anyway — an asterisk read out as "star" is noise, and read out as
            nothing at all, which is the other common handling, is a required field that never
            announced itself. The one field left on this component is required — the clinic beside it
            moved to ClinicField, which says the same thing the same way — so the attribute is
            unconditional. */}
        <span className="field__required" aria-hidden="true">
          *
        </span>
      </label>
      <input
        id={id}
        className={invalid ? 'control control--invalid' : 'control'}
        type="text"
        value={value}
        maxLength={maxLength}
        required
        autoComplete={autoComplete}
        aria-invalid={invalid}
        aria-describedby={invalid ? `${id}-error` : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      {invalid && (
        <p className="field__error" id={`${id}-error`}>
          {errors?.join(' ')}
        </p>
      )}
    </div>
  );
}

interface AnswerFieldProps {
  question: CtInterviewQuestion;
  number: number;
  value: string;
  errors?: string[];
  onChange: (value: string) => void;
}

/**
 * One question and whatever it is answered with.
 *
 * Three questions in four are now a choice between four options, which is a different control and a
 * different piece of markup rather than a variation on the text box: a group of radio buttons needs
 * a `fieldset` and a `legend` to be a group at all, where a text box needs a `label` pointing at it.
 * So this picks between two components rather than branching inside one — partly because sharing the
 * frame would mean a `label` wrapped around a radio group, which labels nothing, and partly because
 * the written half autosizes its textarea from an effect, and an effect behind a branch is a hook
 * that runs on some renders and not others.
 */
function AnswerField(props: AnswerFieldProps) {
  return props.question.format === 'singleChoice' ? <ChoiceField {...props} /> : <WrittenField {...props} />;
}

/** The multiple-choice questions — sections 1 to 3 of the packet. */
function ChoiceField({ question, number, value, errors, onChange }: AnswerFieldProps) {
  const invalid = (errors?.length ?? 0) > 0;
  const controlId = `q-${question.id}`;

  return (
    <fieldset className="interview__question interview__choice">
      <legend className="interview__prompt">
        <span className="interview__number">Q{number}.</span> {question.prompt}
      </legend>

      <div className="interview__options">
        {question.options.map((option) => (
          <label
            className={value === option.id ? 'interview__option interview__option--picked' : 'interview__option'}
            key={option.id}
          >
            <input
              type="radio"
              /* The question id, so the four inputs are one group and the browser's own arrow-key
                 navigation and single-selection apply without anything here implementing them. */
              name={controlId}
              value={option.id}
              checked={value === option.id}
              /* On every input rather than once on the fieldset: a description is announced when the
                 thing it describes takes focus, and what takes focus here is a radio button.
                 `aria-invalid` is not part of this — it is not supported on the radio role, and the
                 message this points at says what is wrong in words anyway. */
              aria-describedby={invalid ? `${controlId}-error` : undefined}
              onChange={() => onChange(option.id)}
            />
            {/* Hidden from the reading: the letter is how the option is referred to on screen and in
                the read-aloud, and read out beside the text it is one more thing to listen past
                before the actual choice arrives. */}
            <span className="interview__option-letter" aria-hidden="true">
              {option.id}
            </span>
            <span className="interview__option-text">{option.text}</span>
          </label>
        ))}
      </div>

      {invalid && (
        <p className="field__error" id={`${controlId}-error`}>
          {errors?.join(' ')}
        </p>
      )}
    </fieldset>
  );
}

/** The written questions — section 4, and whatever short answers an edition still asks for. */
function WrittenField({ question, number, value, errors, onChange }: AnswerFieldProps) {
  const invalid = (errors?.length ?? 0) > 0;
  const controlId = `q-${question.id}`;

  // A live counter the whole time would be noise on an answer nobody writes 4000 characters of.
  // It appears once someone is close enough to the cap for it to matter.
  const nearLimit = value.length > question.maxLength * 0.8;

  // Autosizes as the candidate writes: a fixed box either wastes the page on the short answers or
  // hides the start of the long ones behind an inner scrollbar, and this form is read back by a
  // person who wants to see the whole answer.
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = ref.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [value]);

  return (
    <div className="interview__question">
      <label className="interview__prompt" htmlFor={controlId}>
        <span className="interview__number">Q{number}.</span> {question.prompt}
      </label>

      {question.format === 'shortText' ? (
        <input
          id={controlId}
          className={invalid ? 'control control--invalid' : 'control'}
          type="text"
          value={value}
          maxLength={question.maxLength}
          aria-invalid={invalid}
          aria-describedby={invalid ? `${controlId}-error` : undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <textarea
          id={controlId}
          ref={ref}
          className={invalid ? 'control control--invalid interview__textarea' : 'control interview__textarea'}
          rows={3}
          value={value}
          maxLength={question.maxLength}
          aria-invalid={invalid}
          aria-describedby={invalid ? `${controlId}-error` : undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      )}

      {nearLimit && (
        <p className="interview__counter">
          {value.length} / {question.maxLength} characters
        </p>
      )}

      {invalid && (
        <p className="field__error" id={`${controlId}-error`}>
          {errors?.join(' ')}
        </p>
      )}
    </div>
  );
}
